import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { useTelegram } from '@/hooks/useTelegram';
import { useTelegramBackButton } from '@/hooks/useTelegramBackButton';
import { ARCHETYPE_INFO } from '@/types';
import type { Archetype } from '@/types';
import {
  getUpcomingEvents,
  getPastEvents,
  getEventAttendees,
  checkinToEvent,
  getRecommendedEvents,
  registerForEvent,
  type Event,
  type Attendee,
  type CheckinResult,
  type RecommendedEvent,
} from '@/services/events';

interface EventsPageProps {
  onClose: () => void;
}

type Tab = 'recommended' | 'upcoming' | 'past';

const EVENT_TYPE_INFO: Record<string, { label: string; emoji: string; color: string }> = {
  meetup: { label: 'Встреча', emoji: '👥', color: 'bg-blue-500/20 text-blue-400' },
  workshop: { label: 'Воркшоп', emoji: '🎓', color: 'bg-purple-500/20 text-purple-400' },
  meditation: { label: 'Медитация', emoji: '🧘', color: 'bg-green-500/20 text-green-400' },
  special: { label: 'Особое', emoji: '⭐', color: 'bg-amber-500/20 text-amber-400' },
};

export function EventsPage({ onClose }: EventsPageProps) {
  const [activeTab, setActiveTab] = useState<Tab>('recommended');
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [pastEvents, setPastEvents] = useState<Event[]>([]);
  const [recommended, setRecommended] = useState<RecommendedEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [checkinResult, setCheckinResult] = useState<CheckinResult | null>(null);

  const { hapticFeedback } = useTelegram();

  // Telegram BackButton - close modal or page
  const handleBack = useCallback(() => {
    if (selectedEvent) {
      setSelectedEvent(null);
    } else if (checkinResult) {
      setCheckinResult(null);
    } else {
      onClose();
    }
  }, [selectedEvent, checkinResult, onClose]);
  useTelegramBackButton(handleBack);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setIsLoading(true);
      const [upcoming, past, rec] = await Promise.all([
        getUpcomingEvents(),
        getPastEvents(20),
        getRecommendedEvents().catch(() => ({ events: [] })),
      ]);
      setUpcomingEvents(upcoming.events);
      setPastEvents(past.events);
      setRecommended(rec.events);
    } catch (error) {
      console.error('Failed to load events:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const tabs: { id: Tab; label: string; emoji: string }[] = [
    { id: 'recommended', label: 'Для тебя', emoji: '🎯' },
    { id: 'upcoming', label: 'Скоро', emoji: '📅' },
    { id: 'past', label: 'Прошли', emoji: '📜' },
  ];
  
  return (
    <div className="min-h-screen bg-primary-950 flex flex-col safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="flex flex-col items-center p-4 pb-2 border-b border-primary-800/50">
        <h1 className="text-lg font-semibold text-white">Встречи</h1>
        <p className="text-xs text-primary-500">Оффлайн-мероприятия и реальные встречи</p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-primary-800/50">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-3 flex flex-col items-center gap-1 transition-colors ${
              activeTab === tab.id
                ? 'bg-primary-800/50 text-white'
                : 'text-primary-500'
            }`}
          >
            <span className="text-lg">{tab.emoji}</span>
            <span className="text-xs">{tab.label}</span>
          </button>
        ))}
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
          </div>
        ) : activeTab === 'recommended' ? (
          <RecommendedList events={recommended} onRefresh={loadEvents} />
        ) : activeTab === 'upcoming' ? (
          <EventsList
            events={upcomingEvents}
            onSelect={setSelectedEvent}
            emptyMessage="Нет предстоящих событий"
          />
        ) : (
          <EventsList
            events={pastEvents}
            onSelect={setSelectedEvent}
            emptyMessage="История событий пуста"
            isPast
          />
        )}
      </div>
      
      {/* Event Detail Modal */}
      <AnimatePresence>
        {selectedEvent && (
          <EventDetailModal
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
            onCheckin={async (result) => {
              setCheckinResult(result);
              hapticFeedback('success');
              await loadEvents();
            }}
          />
        )}
      </AnimatePresence>
      
      {/* Checkin Result Modal */}
      <AnimatePresence>
        {checkinResult && (
          <CheckinResultModal
            result={checkinResult}
            onClose={() => setCheckinResult(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// EVENTS LIST
// ============================================

interface EventsListProps {
  events: Event[];
  onSelect: (event: Event) => void;
  emptyMessage: string;
  isPast?: boolean;
}

function EventsList({ events, onSelect, emptyMessage, isPast }: EventsListProps) {
  if (events.length === 0) {
    return (
      <div className="text-center py-12">
        <span className="text-4xl block mb-4">📅</span>
        <p className="text-primary-400">{emptyMessage}</p>
      </div>
    );
  }
  
  // Группируем по дате
  const grouped = events.reduce((acc, event) => {
    const date = new Date(event.startTime).toLocaleDateString('ru-RU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
    if (!acc[date]) acc[date] = [];
    acc[date].push(event);
    return acc;
  }, {} as Record<string, Event[]>);
  
  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([date, dateEvents]) => (
        <div key={date}>
          <h3 className="text-sm font-medium text-primary-400 mb-3 capitalize">{date}</h3>
          <div className="space-y-3">
            {dateEvents.map(event => (
              <EventCard
                key={event.id}
                event={event}
                onClick={() => onSelect(event)}
                isPast={isPast}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================
// EVENT CARD
// ============================================

interface EventCardProps {
  event: Event;
  onClick: () => void;
  isPast?: boolean;
}

function EventCard({ event, onClick, isPast }: EventCardProps) {
  const typeInfo = EVENT_TYPE_INFO[event.eventType] || EVENT_TYPE_INFO.meetup;
  const startTime = new Date(event.startTime);
  const timeStr = startTime.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  
  return (
    <motion.button
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`w-full p-4 rounded-xl text-left transition-all ${
        isPast ? 'bg-primary-900/30 opacity-70' : 'bg-primary-800/50 hover:bg-primary-800/70'
      }`}
    >
      <div className="flex gap-4">
        {/* Time column */}
        <div className="w-14 text-center flex-shrink-0">
          <p className="text-lg font-bold text-white">{timeStr}</p>
          <span className={`text-xs px-2 py-0.5 rounded ${typeInfo.color}`}>
            {typeInfo.emoji}
          </span>
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-white truncate">{event.title}</h3>
          {event.location && (
            <p className="text-sm text-primary-400 truncate">📍 {event.location}</p>
          )}
          <div className="flex items-center gap-3 mt-2 text-xs text-primary-500">
            <span>+{event.xpReward} XP</span>
            <span>👥 {event.checkinCount}{event.maxAttendees ? `/${event.maxAttendees}` : ''}</span>
            {event.isCheckedIn && (
              <span className="text-green-400">✓ Был(а)</span>
            )}
          </div>
        </div>
        
        {/* Status */}
        {!isPast && event.canCheckin && (
          <div className="flex-shrink-0">
            <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">
              Можно чекин
            </span>
          </div>
        )}
      </div>
    </motion.button>
  );
}

// ============================================
// EVENT DETAIL MODAL
// ============================================

interface EventDetailModalProps {
  event: Event;
  onClose: () => void;
  onCheckin: (result: CheckinResult) => void;
}

function EventDetailModal({ event, onClose, onCheckin }: EventDetailModalProps) {
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [isLoadingAttendees, setIsLoadingAttendees] = useState(true);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  
  const { hapticFeedback } = useTelegram();
  const typeInfo = EVENT_TYPE_INFO[event.eventType] || EVENT_TYPE_INFO.meetup;
  
  useEffect(() => {
    loadAttendees();
  }, [event.id]);
  
  const loadAttendees = async () => {
    try {
      const { attendees } = await getEventAttendees(event.id);
      setAttendees(attendees);
    } catch (error) {
      console.error('Failed to load attendees:', error);
    } finally {
      setIsLoadingAttendees(false);
    }
  };
  
  const handleCheckin = async () => {
    setIsCheckingIn(true);
    setLocationError(null);
    
    try {
      let location: { latitude: number; longitude: number } | undefined;
      
      // Получаем геолокацию если требуется
      if (event.requiresLocation) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 10000,
            });
          });
          
          location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
        } catch (geoError) {
          setLocationError('Не удалось получить геолокацию. Разреши доступ к местоположению.');
          setIsCheckingIn(false);
          return;
        }
      }
      
      const result = await checkinToEvent(event.id, location);
      onCheckin(result);
      onClose();
    } catch (error: any) {
      hapticFeedback('error');
      setLocationError(error.message);
    } finally {
      setIsCheckingIn(false);
    }
  };
  
  const startTime = new Date(event.startTime);
  const endTime = new Date(event.endTime);
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-primary-900 rounded-t-3xl w-full max-w-lg max-h-[85vh] flex flex-col safe-area-bottom"
      >
        <div className="p-6 border-b border-primary-800/50">
          <div className="w-12 h-1 bg-primary-700 rounded-full mx-auto mb-4" />
          
          {/* Header */}
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${typeInfo.color}`}>
              <span className="text-2xl">{typeInfo.emoji}</span>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white">{event.title}</h2>
              <p className="text-sm text-primary-400">{typeInfo.label}</p>
            </div>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Time & Location */}
          <div className="space-y-2">
            <div className="flex items-center gap-3 text-primary-300">
              <span>🕐</span>
              <span>
                {startTime.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })},{' '}
                {startTime.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })} —{' '}
                {endTime.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            
            {event.location && (
              <div className="flex items-center gap-3 text-primary-300">
                <span>📍</span>
                <span>{event.location}</span>
              </div>
            )}
            
            <div className="flex items-center gap-3 text-primary-300">
              <span>⭐</span>
              <span>+{event.xpReward} XP за участие</span>
            </div>
          </div>
          
          {/* Description */}
          {event.description && (
            <p className="text-primary-300">{event.description}</p>
          )}
          
          {/* Attendees */}
          <div>
            <h3 className="text-sm font-medium text-primary-400 mb-3">
              Участники ({event.checkinCount}{event.maxAttendees ? `/${event.maxAttendees}` : ''})
            </h3>
            
            {isLoadingAttendees ? (
              <div className="text-center py-4 text-primary-500">Загрузка...</div>
            ) : attendees.length === 0 ? (
              <div className="text-center py-4 text-primary-500">Пока никто не зачекинился</div>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {attendees.map(attendee => {
                  const archetypeInfo = attendee.archetype
                    ? ARCHETYPE_INFO[attendee.archetype as Archetype]
                    : null;
                  
                  return (
                    <div
                      key={attendee.oderId}
                      className="flex items-center gap-3 p-2 rounded-lg bg-primary-800/30"
                    >
                      <div className="w-8 h-8 rounded-full bg-primary-700/50 flex items-center justify-center">
                        <span className="text-sm">{archetypeInfo?.emoji || '👤'}</span>
                      </div>
                      <span className="text-sm text-primary-200">
                        {attendee.characterName || attendee.userName}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Error */}
          {locationError && (
            <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {locationError}
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="p-6 border-t border-primary-800/50 space-y-3">
          {event.isCheckedIn ? (
            <div className="text-center py-3 text-green-400">
              ✓ Ты уже зачекинился на это событие
            </div>
          ) : event.canCheckin ? (
            <Button
              onClick={handleCheckin}
              loading={isCheckingIn}
              fullWidth
            >
              {event.requiresLocation ? '📍 Чекин по геолокации' : '✓ Чекин'}
            </Button>
          ) : (
            <div className="text-center py-3 text-primary-500 text-sm">
              Чекин недоступен
            </div>
          )}
          
          <Button onClick={onClose} variant="ghost" fullWidth>
            Закрыть
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// RECOMMENDED LIST
// ============================================

interface RecommendedListProps {
  events: RecommendedEvent[];
  onRefresh: () => void;
}

function RecommendedList({ events, onRefresh }: RecommendedListProps) {
  const [registering, setRegistering] = useState<string | null>(null);
  const { hapticFeedback } = useTelegram();

  if (events.length === 0) {
    return (
      <div className="text-center py-12">
        <span className="text-4xl block mb-4">🎯</span>
        <p className="text-primary-400">Пока нет рекомендаций</p>
        <p className="text-xs text-primary-500 mt-1">Пройди батареи и начни этапы, чтобы получить персональные рекомендации</p>
      </div>
    );
  }

  const handleRegister = async (eventId: string) => {
    setRegistering(eventId);
    try {
      await registerForEvent(eventId);
      hapticFeedback('success');
      onRefresh();
    } catch (e: any) {
      hapticFeedback('error');
      alert(e.message);
    } finally {
      setRegistering(null);
    }
  };

  return (
    <div className="space-y-3">
      {events.map(event => {
        const dateStr = new Date(event.startsAt).toLocaleDateString('ru-RU', {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        });

        return (
          <motion.div
            key={event.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-primary-800/50 rounded-xl p-4 space-y-3"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-white">{event.title}</h3>
                <p className="text-xs text-primary-400 mt-1">{dateStr}</p>
                {event.location && (
                  <p className="text-xs text-primary-500 mt-0.5">📍 {event.location}</p>
                )}
              </div>
              <div className="text-right flex-shrink-0 ml-2">
                <span className="text-xs bg-primary-700/50 px-2 py-0.5 rounded text-primary-300">
                  +{event.xpReward} XP
                </span>
              </div>
            </div>

            {/* Characteristic tags */}
            {event.targetChars && event.targetChars.length > 0 && (
              <div className="flex gap-1 flex-wrap">
                {event.targetChars.map(ch => (
                  <span key={ch} className="text-xs px-1.5 py-0.5 bg-primary-700/40 text-primary-300 rounded">
                    {ch}
                  </span>
                ))}
              </div>
            )}

            {/* Format & social info */}
            <div className="flex items-center gap-3 text-xs text-primary-500">
              {event.format && <span>{event.format}</span>}
              {event.socialFormat && <span>{event.socialFormat}</span>}
              <span>
                👥 {event.currentParticipants}{event.maxParticipants ? `/${event.maxParticipants}` : ''}
              </span>
              <span className="ml-auto text-primary-600">
                {Math.round(event.score * 100)}% совпадение
              </span>
            </div>

            {/* Register button */}
            <Button
              onClick={() => handleRegister(event.id)}
              loading={registering === event.id}
              fullWidth
              variant="primary"
            >
              Записаться
            </Button>
          </motion.div>
        );
      })}
    </div>
  );
}

// ============================================
// CHECKIN RESULT MODAL
// ============================================

interface CheckinResultModalProps {
  result: CheckinResult;
  onClose: () => void;
}

function CheckinResultModal({ result, onClose }: CheckinResultModalProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-primary-900 rounded-2xl p-6 w-full max-w-sm border border-primary-700/50"
      >
        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.1 }}
            className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center mb-4"
          >
            <span className="text-4xl">✓</span>
          </motion.div>
          
          <h2 className="text-xl font-bold text-white mb-2">Чекин успешен!</h2>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-primary-800/50 rounded-xl p-4 mb-4"
          >
            <p className="text-3xl font-bold text-primary-300">+{result.xpGained} XP</p>
          </motion.div>
          
          {result.levelUp && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-r from-creator/20 to-creator/10 border border-creator/30 rounded-xl p-4 mb-4"
            >
              <p className="text-creator font-bold">🎉 Новый уровень {result.newLevel}!</p>
            </motion.div>
          )}
          
          {result.questsCompleted.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-amber-500/20 border border-amber-500/30 rounded-xl p-3 mb-4"
            >
              <p className="text-amber-400 text-sm">
                📋 Квестов выполнено: {result.questsCompleted.length}
              </p>
            </motion.div>
          )}
          
          <Button onClick={onClose} fullWidth>
            Отлично!
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}
